import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  TextField,
  Tooltip,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useCanSendTestEvent } from '@/services/permissions';
import { isEmptyString } from '@/utils/string';
import {
  FORM_SUBMIT_EVENT,
  formEventFields,
  formEventFieldsToJson,
  type SendEventForm,
} from '../utils/form-event-data';
import { EventDataEditor } from './event-data-editor';
import { FormEventDataEditor } from './form-event-data-editor';
import { RecipientPicker, type SendEventRecipient } from './recipient-picker';
import { useSendEventForm } from './use-send-event-form';

/** The footer's Send button submits the fields in the body. */
const SEND_EVENT_FORM_ID = 'send-event-form';

/**
 * Send one event for one profile.
 *
 * Every surface that offers this opens the same modal: the events browser,
 * an event's own page, and the activity feed's Resend. What differs between
 * them is only how much is known up front — a profile, a name, a payload —
 * so all three are data the caller passes in rather than modes the modal
 * knows about.
 */

export type SendEventModalData = {
  /**
   * The workspace the event is sent in. Passed in rather than read here: a
   * managed modal mounts outside the app's own tree, so it must not depend
   * on where that tree happens to be.
   */
  workspaceId: number;
  /** The profile to send for, when the caller already knows it. */
  recipient?: SendEventRecipient | null;
  eventName?: string;
  /** Pretty-printed JSON of the event attributes. */
  eventData?: string;
  /**
   * The form to send a test submission of. With a form, the event is always
   * `form_submit` and its data is one row per form field, so `eventName` and
   * `eventData` are not read.
   */
  form?: SendEventForm;
  /** Where the modal was opened from. Recorded on every analytics event; a surface with none records none. */
  source?: string;
  /** The viewer may not send: the form is inert and Send says why. */
  isReadOnly?: boolean;
  /** Called once the backend has had time to record the event. */
  onSent?: () => void;
};

/**
 * Whether this send retries one that was already recorded: the caller knows
 * both the profile and the event name. Analytics splits the two so "send an
 * event" is not read as retrying one.
 */
export function isResend(data: Pick<SendEventModalData, 'eventName' | 'recipient'>): boolean {
  return !isEmptyString(data.eventName ?? '') && data.recipient != null;
}

export function SendEventModal({ data, modalState, onClose }: PlumaManagedModalProps<SendEventModalData>) {
  const canSend = useCanSendTestEvent();

  const terms = {
    singular: 'person',
    titleSingular: 'Person',
    titlePlural: 'People',
  };
  const isDisabled = (data.isReadOnly ?? false) || !canSend;
  const formFields = data.form == null ? null : formEventFields(data.form);

  const form = useSendEventForm({
    workspaceId: data.workspaceId,
    source: data.source,
    isResend: isResend(data),
    isDisabled,
    initial: {
      recipient: data.recipient ?? null,
      name: data.form == null ? (data.eventName ?? '') : FORM_SUBMIT_EVENT,
      data: formFields == null ? (data.eventData ?? '') : formEventFieldsToJson(formFields),
    },
    sendForm: data.form,
    personSingular: terms.singular,
    onSent: data.onSent,
    onClose,
  });

  return (
    <Modal
      state={modalState}
      title={data.form == null ? 'Send an event' : `Send ${FORM_SUBMIT_EVENT} event`}
      onClose={onClose}
    >
      <ModalBody>
        <Stack gap="250">
          <RecipientPicker
            workspaceId={data.workspaceId}
            recipient={form.recipient}
            onChange={form.setRecipient}
            terms={terms}
            isDisabled={isDisabled}
          />

          {/* The recipient search stays outside this form: EmailSearch renders
              its own form, which Enter submits to run the search, and forms
              must not nest (see EmailSearch's doc comment). */}
          <Form id={SEND_EVENT_FORM_ID} onSubmit={() => form.submit()}>
            {/* A form submission's name is fixed and its attributes are the
                form's own, so neither the name nor the free editors are offered. */}
            {formFields == null ? (
              <Stack gap="250">
                <TextField
                  label="Event name"
                  placeholder="e.g. account_update, cart_checkout, cart_abandon"
                  value={form.name}
                  error={form.nameError}
                  isDisabled={isDisabled}
                  onInput={(event) => form.setName(event.target.value)}
                  onBlur={form.checkName}
                />

                <EventDataEditor
                  data={form.data}
                  onChange={form.setData}
                  editorType={form.editorType}
                  onEditorTypeChange={form.setEditorType}
                  error={form.dataError}
                  isDisabled={isDisabled}
                />
              </Stack>
            ) : (
              <FormEventDataEditor
                initialFields={formFields}
                onChange={form.setData}
                error={form.dataError}
                isDisabled={isDisabled}
              />
            )}
          </Form>
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" isDisabled={form.isSending} onClick={() => onClose()}>
          Cancel
        </Button>
        {/* Disabled while something is missing, which the form rule allows
            where the flip is what says a field is required: the tooltip beside
            it names what is missing, and the page this replaces disables the
            same button for the same reasons. */}
        <Tooltip isDisabled={form.blockedReason == null} content={form.blockedReason ?? ''} isCustom={true}>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              type="submit"
              form={SEND_EVENT_FORM_ID}
              variant="primary"
              isDisabled={form.blockedReason != null}
              isLoading={form.isSending}
            >
              {data.form == null ? 'Send event' : `Send ${FORM_SUBMIT_EVENT} event`}
            </Button>
          )}
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
}
